import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
} from "react";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";
import { Link2, Search, X } from "lucide-react";
import { Skeleton, cx, useAppNavigate, useDebounced, useMediaQuery } from "@amber/ui";
import type { SearchResponse } from "@amber/application/contracts";
import { searchQuery } from "../../views/search/queries";
import { PLACEHOLDER } from "../../views/search/hits";
import { GroupHeading, None, OptionRow, buildOptions, type Option, type OptionGroup } from "./options";

const DROPDOWN_LIMIT = 5;
const DEBOUNCE_MS = 150;

type Place = { left: number; top: number; width: number; maxHeight: number };

function useSlashShortcut(focusSearch: () => void) {
  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key !== "/" || e.metaKey || e.ctrlKey || e.altKey) return;
      const el = document.activeElement;
      if (el instanceof HTMLElement && (/^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName) || el.isContentEditable)) return;
      if (document.querySelector('[role="dialog"]')) return;
      e.preventDefault();
      focusSearch();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
    };
  }, [focusSearch]);
}

function usePlacement(
  open: boolean,
  expanded: boolean,
  compact: boolean,
  boxRef: RefObject<HTMLDivElement | null>,
): Place | null {
  const [place, setPlace] = useState<Place | null>(null);
  useLayoutEffect(() => {
    if (!open) return;
    const measure = () => {
      const r = boxRef.current?.getBoundingClientRect();
      if (!r) return;
      const width = Math.min(600, window.innerWidth - 16);
      const left = Math.min(Math.max(8, r.left), window.innerWidth - width - 8);
      const top = r.bottom + 6;
      setPlace({ left, top, width, maxHeight: Math.max(200, window.innerHeight - top - 40) });
    };
    measure();
    window.addEventListener("resize", measure);
    return () => {
      window.removeEventListener("resize", measure);
    };
  }, [open, expanded, compact]);
  return place;
}

function useDismiss(
  open: boolean,
  boxRef: RefObject<HTMLDivElement | null>,
  popRef: RefObject<HTMLDivElement | null>,
  close: () => void,
  setExpanded: (expanded: boolean) => void,
) {
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (boxRef.current?.contains(target) || popRef.current?.contains(target)) return;
      close();
      setExpanded(false);
    };
    document.addEventListener("pointerdown", onDown, true);
    return () => {
      document.removeEventListener("pointerdown", onDown, true);
    };
  }, [open, close]);
}

export function GlobalSearch() {
  const navigate = useAppNavigate();
  const compact = useMediaQuery("(max-width: 800px)");
  const [value, setValue] = useState("");
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [active, setActive] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const listId = useId();
  const q = useDebounced(value.trim(), DEBOUNCE_MS);
  const result = useQuery(searchQuery({ q }, DROPDOWN_LIMIT));
  const data = q.length > 0 && result.data?.q === q ? result.data : undefined;
  const { groups } = useMemo(() => buildOptions(data, q), [data, q]);
  const flat = useMemo(() => groups.flatMap((g) => g.options), [groups]);
  const optionId = (o: Option) => `${listId}-${o.id}`;

  const close = useCallback(() => {
    setOpen(false);
    setActive(-1);
  }, []);
  const focusSearch = useCallback(() => {
    setExpanded(true);
    setOpen(true);
    requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    });
  }, []);

  useEffect(() => {
    setActive(-1);
  }, [q]);

  useSlashShortcut(focusSearch);
  const place = usePlacement(open, expanded, compact, boxRef);
  useDismiss(open, boxRef, popRef, close, setExpanded);

  const pick = (o: Option | undefined) => {
    if (!o?.href || o.disabled) return;
    close();
    setExpanded(false);
    setValue("");
    inputRef.current?.blur();
    navigate(o.href);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      setOpen(true);
      if (flat.length === 0) return;
      const step = e.key === "ArrowDown" ? 1 : -1;
      setActive((a) => (a < 0 ? (step > 0 ? 0 : flat.length - 1) : (a + step + flat.length) % flat.length));
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      const target = flat[active < 0 ? 0 : active];
      if (target && data) pick(target);
      else if (value.trim()) pick({ id: "page", href: `/search?q=${encodeURIComponent(value.trim())}`, content: null });
      return;
    }
    if (e.key === "Escape") {
      e.preventDefault();
      if (open) close();
      else if (value) setValue("");
      else {
        setExpanded(false);
        inputRef.current?.blur();
      }
      return;
    }
    if (e.key === "Tab") {
      close();
      setExpanded(false);
    }
  };

  useEffect(() => {
    if (active < 0) return;
    const o = flat[active];
    if (o) document.getElementById(optionId(o))?.scrollIntoView({ block: "nearest" });
  });

  const activeOption = active >= 0 ? flat[active] : undefined;
  const showBox = !compact || expanded;
  const loading = value.trim().length > 0 && (q !== value.trim() || result.isFetching) && !data;

  return (
    <div className={cx("flex min-w-0 items-center", compact ? "flex-none" : "flex-[0_1000_380px]")}>
      {compact && !expanded ? (
        <button
          type="button"
          aria-label={PLACEHOLDER}
          title="Search (/)"
          onClick={focusSearch}
          className="inline-flex size-8 items-center justify-center rounded-lg border border-border bg-bg text-muted hover:bg-surface-2 hover:text-fg"
        >
          <Search size={15} strokeWidth={1.8} aria-hidden="true" />
        </button>
      ) : null}
      {showBox ? (
        <SearchBox
          boxRef={boxRef}
          inputRef={inputRef}
          compact={compact}
          open={open}
          listId={listId}
          activeDescendant={activeOption ? optionId(activeOption) : undefined}
          value={value}
          setValue={setValue}
          setOpen={setOpen}
          onKeyDown={onKeyDown}
        />
      ) : null}
      {open && place
        ? createPortal(
            <div
              ref={popRef}
              style={{ left: place.left, top: place.top, width: place.width, maxHeight: place.maxHeight }}
              className="fixed z-search-results flex flex-col overflow-hidden rounded-xl border border-border bg-bg text-ui leading-[1.4] text-fg shadow-pop"
            >
              <div
                id={listId}
                role="listbox"
                aria-label="Search results"
                className="min-h-0 overflow-auto overscroll-contain pt-0.5 pb-1.5"
              >
                <ResultList
                  value={value}
                  loading={loading}
                  data={data}
                  q={q}
                  groups={groups}
                  flat={flat}
                  active={active}
                  setActive={setActive}
                  listId={listId}
                  optionId={optionId}
                  pick={pick}
                />
              </div>
              <KeyHints />
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}

function SearchBox({
  boxRef,
  inputRef,
  compact,
  open,
  listId,
  activeDescendant,
  value,
  setValue,
  setOpen,
  onKeyDown,
}: {
  boxRef: RefObject<HTMLDivElement | null>;
  inputRef: RefObject<HTMLInputElement | null>;
  compact: boolean;
  open: boolean;
  listId: string;
  activeDescendant: string | undefined;
  value: string;
  setValue: (value: string) => void;
  setOpen: (open: boolean) => void;
  onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void;
}) {
  return (
    <div
      ref={boxRef}
      role="search"
      className={cx(
        "flex h-8 w-full min-w-0 items-center gap-[7px] rounded-lg border border-border bg-surface-2 pr-[5px] pl-[9px] text-muted focus-within:border-link focus-within:bg-bg focus-within:shadow-[0_0_0_3px_var(--color-accent-soft)]",
        compact && "absolute top-2 right-2 left-2 z-compact-search h-9 w-auto bg-bg shadow-pop",
      )}
    >
      <Search size={14} strokeWidth={1.8} aria-hidden="true" className="flex-none" />
      <input
        ref={inputRef}
        type="search"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={activeDescendant}
        aria-label={PLACEHOLDER}
        placeholder={PLACEHOLDER}
        autoComplete="off"
        spellCheck={false}
        value={value}
        onChange={(e) => {
          setValue(e.target.value);
          setOpen(true);
        }}
        onFocus={() => {
          setOpen(true);
        }}
        onKeyDown={onKeyDown}
        className="h-full min-w-0 flex-1 border-0 bg-transparent p-0 text-ui text-fg outline-none placeholder:text-muted [&::-webkit-search-cancel-button]:hidden"
      />
      {value ? (
        <button
          type="button"
          aria-label="Clear search"
          onMouseDown={(e) => {
            e.preventDefault();
          }}
          onClick={() => {
            setValue("");
            inputRef.current?.focus();
          }}
          className="grid size-[22px] flex-none place-items-center rounded-[5px] text-muted hover:bg-surface-3 hover:text-fg"
        >
          <X size={12} aria-hidden="true" />
        </button>
      ) : (
        <kbd
          aria-hidden="true"
          title="Press / to search"
          className="flex-none rounded-sm border border-border px-[5px] font-mono text-2xs leading-4 text-muted max-compact:hidden"
        >
          /
        </kbd>
      )}
    </div>
  );
}

function ResultList({
  value,
  loading,
  data,
  q,
  groups,
  flat,
  active,
  setActive,
  listId,
  optionId,
  pick,
}: {
  value: string;
  loading: boolean;
  data: SearchResponse | undefined;
  q: string;
  groups: OptionGroup[];
  flat: Option[];
  active: number;
  setActive: (index: number) => void;
  listId: string;
  optionId: (o: Option) => string;
  pick: (o: Option | undefined) => void;
}) {
  if (value.trim().length === 0) {
    return (
      <div role="presentation" className="px-3 pt-2.5 pb-1 text-md text-muted">
        Search item titles, transcripts and people, or paste a link to check whether we already have it.
      </div>
    );
  }
  if (loading) {
    return (
      <div role="presentation" className="flex flex-col gap-2 px-3 py-3">
        <Skeleton className="h-3.5 w-2/3" />
        <Skeleton className="h-3.5 w-1/2" />
      </div>
    );
  }
  return (
    <>
      {data?.url ? (
        <div role="presentation" className="mx-3 mt-2 mb-0.5 font-mono text-sm break-all text-muted">
          <Link2 size={11} aria-hidden="true" className="mr-1.5 inline align-[-1px]" />
          {value.trim()}
        </div>
      ) : null}
      {groups.map((g) => (
        <div key={g.key} role="group" aria-labelledby={`${listId}-g-${g.key}`}>
          <GroupHeading
            id={`${listId}-g-${g.key}`}
            label={g.label}
            {...(g.count === undefined ? {} : { count: g.count })}
          />
          {"note" in g ? g.note : null}
          {g.options.map((o) => {
            const index = flat.indexOf(o);
            return (
              <OptionRow
                key={o.id}
                id={optionId(o)}
                active={index === active}
                disabled={o.disabled}
                onPick={() => {
                  pick(o);
                }}
                onHover={() => {
                  if (index !== active) setActive(index);
                }}
              >
                {o.content}
              </OptionRow>
            );
          })}
        </div>
      ))}
      {data && !data.url && groups.length === 0 ? (
        <None title={`Nothing matches “${q}”`}>
          Search covers item titles, transcripts, and people with their aliases. Paste a link to check whether it’s
          collected.
        </None>
      ) : null}
    </>
  );
}

function KeyHints() {
  return (
    <div aria-hidden="true" className="flex flex-wrap gap-3.5 border-t border-divider px-3 py-1.5 text-xs text-muted">
      <span>
        <Kbd>↑</Kbd>
        <Kbd>↓</Kbd> move
      </span>
      <span>
        <Kbd>Enter</Kbd> open
      </span>
      <span>
        <Kbd>Esc</Kbd> close
      </span>
    </div>
  );
}

function Kbd({ children }: { children: ReactNode }) {
  return <kbd className="mr-0.5 rounded-sm border border-border px-1 font-mono text-2xs">{children}</kbd>;
}
